import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Thread } from '../../types/thread';
import { useSearchParams } from "react-router-dom";
import { useLang } from '../../Contexts/LanguageContext';

const RTL_RE = /[֑-߿יִ-﷽ﹰ-ﻼ]/;
const textDir = (s: string): 'rtl' | 'ltr' => RTL_RE.test(s) ? 'rtl' : 'ltr';

const ThreadList: React.FC<{ threads: Thread[], onRename?: (id: string, newName: string) => void, onDelete?: (id: string) => void }> = ({ threads, onRename, onDelete }) => {

    const { isRTL } = useLang();
    const [searchParams, setSearchParams] = useSearchParams();

    const threadIdFromQuery = searchParams.get("thread_id");

    const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(threadIdFromQuery);

    React.useEffect(() => {
        setSelectedThreadId(threadIdFromQuery);
    }, [threadIdFromQuery]);

    const handleSelect = (id: string) => {
        setSelectedThreadId(id);
        searchParams.set("thread_id", id);
        setSearchParams(searchParams);
    };

    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
    const [menuThreadId, setMenuThreadId] = React.useState<string | null>(null);

    const openMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setMenuThreadId(id);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuThreadId(null);
    };

    const handleRename = () => {
        if (!menuThreadId) return closeMenu();
        const newName = window.prompt('Enter new thread name:');
        if (newName && newName.trim().length > 0) {
            if (onRename) onRename(menuThreadId, newName.trim());
            else console.log('Rename requested:', menuThreadId, newName.trim());
        }
        closeMenu();
    };

    const handleDelete = () => {
        if (!menuThreadId) return closeMenu();
        const ok = window.confirm('Delete this thread? This action cannot be undone.');
        if (ok) {
            if (onDelete) onDelete(menuThreadId);
            else console.log('Delete requested:', menuThreadId);
        }
        closeMenu();
    };

    return (
        <Box dir="ltr" sx={{ width: '100%', overflowX: 'hidden' }}>
            <List sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
                {threads.map((thread, index) => {
                    // Lay rows out by the website language (RTL toggle): Arabic → accent
                    // bar on the right and the ⋮ menu on the left; English → mirrored.
                    const rtlItem = isRTL;
                    return (
                    <React.Fragment key={thread._id}>
                        <ListItem
                            alignItems="flex-start"
                            onClick={() => handleSelect(thread._id)}
                            sx={{
                                textAlign: 'left',
                                borderRadius: 0,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s, border-left 0.2s',
                                // Accent on the start side. RtlProvider (stylis-plugin-rtl)
                                // auto-mirrors physical CSS, so this left border renders on
                                // the RIGHT in Arabic — do not flip it manually.
                                borderLeft: selectedThreadId === thread._id
                                    ? '3px solid #4EC9B0'
                                    : '3px solid transparent',
                                bgcolor: selectedThreadId === thread._id
                                    ? 'rgba(78, 201, 176, 0.15)'
                                    : 'transparent',
                                '&:hover': {
                                    bgcolor: selectedThreadId === thread._id
                                        ? 'rgba(78, 201, 176, 0.2)'
                                        : 'rgba(255,255,255,0.06)',
                                },
                                '&:hover .thread-actions': {
                                    visibility: 'visible',
                                    opacity: 1,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexDirection: rtlItem ? 'row-reverse' : 'row' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <span
                                        dir={textDir(thread.configurable.thread_name || '')}
                                        style={{
                                            display: 'block',
                                            color: selectedThreadId === thread._id ? '#4EC9B0' : 'white',
                                            fontWeight: selectedThreadId === thread._id ? 600 : 400,
                                            fontSize: '0.875rem',
                                            lineHeight: 1.5,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            textAlign: textDir(thread.configurable.thread_name || '') === 'rtl' ? 'right' : 'left',
                                        }}
                                    >
                                        {thread.configurable.thread_name}
                                    </span>
                                </Box>
                                <IconButton
                                    className="thread-actions"
                                    aria-label="thread actions"
                                    size="small"
                                    onClick={(e) => openMenu(e, thread._id)}
                                    sx={{ visibility: 'hidden', opacity: 0, transition: 'opacity 0.12s' }}
                                >
                                    <MoreVertIcon sx={{ color: 'white' }} fontSize="small" />
                                </IconButton>
                            </Box>
                        </ListItem>
                        {index < threads.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                    );
                })}
            </List>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem onClick={handleRename}>Rename</MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>Delete</MenuItem>
            </Menu>
        </Box>
    );
};

export default ThreadList;
